// Game.ts
import type { Game as BoardGame } from "boardgame.io";
import { INVALID_MOVE } from "boardgame.io/core";
import { nanoid } from "nanoid";
import HexGrid, {
  makeHexGrid,
  getCell,
  getCellNeighbors,
  setCell,
  getNewCellValues,
} from "buzzwords-shared/hexgrid";
import * as R from "ramda";
import { HexCoord } from "./types";

// Dynamic import cache for words module
let wordsModuleCache: { WordsSet: typeof import("./words").WordsSet; words: typeof import("./words").words } | null = null;
let wordsModulePromise: Promise<typeof import("./words")> | null = null;

function getWordsModule() {
  if (!wordsModuleCache) {
    throw new Error("Words module not loaded. Call loadWordsModule() first.");
  }
  return wordsModuleCache;
}

// Load the words module dynamically
function loadWordsModule() {
  if (!wordsModulePromise) {
    wordsModulePromise = import("./words").then((module) => {
      wordsModuleCache = {
        WordsSet: module.WordsSet,
        words: module.words,
      };
      return module;
    });
  }
  return wordsModulePromise;
}

// Eagerly load the module
loadWordsModule();
import { canMakeAValidWord, isValidWord } from "./alphaHelpers";
import { getCellsToBeReset, willBecomeOwned } from "./gridHelpers";
import Cell from "./cell";
import { getBotMove } from "./bot";

export interface BuzzwordsGameState {
  id: string;
  grid: HexGrid;
  difficulty: number;
  selection: HexCoord[];
}

export function getWordFromMove(G: BuzzwordsGameState, move: HexCoord[]) {
  let word = "";
  for (const coord of move) {
    // All we know is this is an array. Need to validate each item.
    if (!R.is(Object, coord) || !R.has("q", coord) || !R.has("r", coord)) {
      return INVALID_MOVE;
    }
    const validCoord = coord as HexCoord;

    const cell = getCell(G.grid, validCoord.q, validCoord.r);
    if (cell && cell.owner == 2 && cell.value) {
      word += cell.value;
    } else {
      return INVALID_MOVE;
    }
  }
  return word;
}

export const Buzzwords: BoardGame<BuzzwordsGameState> = {
  name: "buzzwords",
  setup: ({ ctx, ...plugins }, setupData) => {
    const game: BuzzwordsGameState = {
      id: nanoid(),
      grid: makeHexGrid(),
      difficulty: setupData?.difficulty ?? 5,
      selection: [],
    };
    const neighbors = [
      ...getCellNeighbors(game.grid, -2, -1),
      ...getCellNeighbors(game.grid, 2, 1),
    ];
    const newValues = getNewCellValues([], 12, getWordsModule().words);
    let i = 0;
    for (const cell of neighbors) {
      cell.value = newValues[i];
      i++;
      game.grid = setCell(game.grid, cell);
    }
    game.grid["-2,-1"].capital = true;
    game.grid["-2,-1"].owner = 0;
    game.grid["2,1"].capital = true;
    game.grid["2,1"].owner = 1;
    return game;
  },
  turn: {
    onEnd: ({ G, ctx, events, random, ...plugins }) => {
      // Check if the game needs to end?
      // events.endGame();
    },
    minMoves: 1,
  },
  maxPlayers: 2,
  minPlayers: 2,
  moves: {
    updateSelection: ({ G }, selection: HexCoord[]) => {
      G.selection = selection;
    },
    // TODO: get this to expose its type externally
    playWord: (
      { G, ctx, playerID, events, random, ...plugins },
      move: HexCoord[]
    ) => {
      console.log("🚀 ~ playerID:", playerID);
      const word = getWordFromMove(G, move);
      if (word === INVALID_MOVE) {
        return INVALID_MOVE;
      }
      if (!isValidWord(word, getWordsModule().WordsSet)) {
        return INVALID_MOVE;
      }

      // Clear selection after validating the move
      G.selection = [];

      // TODO: are we storing playerID as 0 or 1?
      // TODO: If not, we need to map playerID back to 0 or 1
      // TODO: or rewrite all the gridHelpers
      const turn = parseInt(playerID) as 0 | 1;
      let capitalCaptured = false;

      // Make all tiles adjacent to move neutral and active
      const resetTiles = getCellsToBeReset(G.grid, move, turn);

      // Parsed word, checked validity of move and word etc.
      // Have to check for what's attached to current territory to see what to expand
      // Have to check from above to see what is adjacent to enemy territory to see what to remove
      // change whose turn it is
      const toBecomeOwned = willBecomeOwned(G.grid, move, turn);

      const toBeReset = R.difference(resetTiles, toBecomeOwned);

      for (const cell of toBecomeOwned) {
        cell.owner = turn;
        if (cell.owner == turn) {
          cell.value = "";
        }

        setCell(G.grid, cell);
      }

      const keys = R.difference(
        R.difference(
          Object.keys(G.grid),
          toBeReset.map((cell) => `${cell.q},${cell.r}`)
        ),
        toBecomeOwned.map((cell) => `${cell.q},${cell.r}`)
      );

      const grid = G.grid;
      const letters = keys.map((k) => grid[k].value).filter(Boolean);

      const opponentKeys = Object.entries(G.grid)
        .filter(([k, c]) => c.owner == Number(!turn))
        .map(([k, c]) => k);

      try {
        const newCellValues = getNewCellValues(
          letters,
          toBeReset.length,
          getWordsModule().words,
          random
        );
        for (let i = 0; i < toBeReset.length; i++) {
          const tile = toBeReset[i];
          tile.owner = 2;
          if (tile.capital) {
            capitalCaptured = true;
            tile.capital = false;
          }
          tile.value = newCellValues[i];
          setCell(G.grid, tile);
        }
      } catch (e) {
        // No possible combinations. Need to regenerate the whole board!!
        const newLetterCount = letters.length + toBeReset.length;
        const newCellValues = getNewCellValues(
          [],
          newLetterCount,
          getWordsModule().words,
          random
        );
        for (const tile of keys
          .map((k) => grid[k])
          .filter((k) => Boolean(k.value))) {
          tile.owner = 2;
          tile.value = newCellValues[0];
          newCellValues.splice(0, 1);
          setCell(G.grid, tile);
        }
        for (const tile of toBeReset) {
          tile.owner = 2;
          if (tile.capital) {
            capitalCaptured = true;
            tile.capital = false;
          }
          tile.value = newCellValues[0];
          newCellValues.splice(0, 1);
          setCell(G.grid, tile);
        }
      }

      const gameOver =
        R.difference(
          opponentKeys,
          [...toBeReset, ...toBecomeOwned].map((c) => `${c.q},${c.r}`)
        ).length === 0;

      if (gameOver) {
        events.endGame(playerID);
        return;
      }

      for (const cell of Object.values(G.grid)) {
        if (cell.owner == 2) {
          const neighbors = getCellNeighbors(G.grid, cell.q, cell.r);
          const playerNeighbors = neighbors.filter((c) => c.owner != 2);
          if (!playerNeighbors.length) {
            cell.value = "";
            setCell(G.grid, cell);
          }
        }
      }

      const cells = G.grid;
      const opponentCells: Cell[] = [];
      let opponentHasCapital = false;
      for (const cell of Object.values(cells)) {
        if (cell.owner == Number(!turn)) {
          opponentCells.push(cell);
          if (cell.capital) {
            opponentHasCapital = true;
            break;
          }
        }
      }

      // If opponent has no capital at the end of your turn
      // but you didn't capture their capital this turn
      // randomly assign one of their cells to be capital
      if (!capitalCaptured && !opponentHasCapital) {
        const newCapital =
          opponentCells[Math.floor(random.Number() * opponentCells.length)];
        newCapital.capital = true;
        setCell(G.grid, newCapital);
      }

      // Your turn is over unless you captured the capital
      if (capitalCaptured) {
        events.endTurn({ next: playerID });
      } else {
        events.endTurn();
      }
    },
  },
  ai: {
    enumerate(G, ctx) {
      try {
        const botMove = getBotMove(G.grid, {
          difficulty: G.difficulty,
          words: getWordsModule().words,
          bannedWords: {},
        });
        return [{ move: "playWord", args: [botMove] }];
      } catch (e) {
        // If bot can't find a move, pass
        return [{ move: "pass", args: [] }];
      }
    },
  },
};

const getUniqPermutations = (coordsPerLetter: HexCoord[][]): HexCoord[][] =>
  R.pipe(
    R.reduce(R.xprod),
    R.map(R.flatten),
    R.filter(onlyHasUniqElements)
    // @ts-expect-error ramda bs
  )(R.head(coordsPerLetter), R.tail(coordsPerLetter));

const onlyHasUniqElements = (arr: any) => {
  return R.uniq(arr).length === arr.length;
};
